/** 区间因子 options：兼容 gte/gt、lte/lt 及字符串 JSON */

export type RangeBoundOp = 'gt' | 'gte' | 'lt' | 'lte'

export interface ParsedRangeBounds {
  left: string
  right: string
  leftOp: 'gt' | 'gte'
  rightOp: 'lt' | 'lte'
  /** 展示用比较符（后端 descript 或 options 附带字段） */
  leftCompare?: string
  rightCompare?: string
}

export type SelectedRangeState = {
  left?: string
  right?: string
  leftOp?: RangeBoundOp
  rightOp?: RangeBoundOp
  leftCompare?: string
  rightCompare?: string
}

export function parseRangeOptionRaw(raw: unknown): Record<string, unknown> | null {
  if (raw == null) return null
  if (typeof raw === 'string') {
    const s = raw.trim()
    if (!s) return null
    try {
      const obj = JSON.parse(s)
      return obj && typeof obj === 'object' ? (obj as Record<string, unknown>) : null
    } catch {
      return null
    }
  }
  if (typeof raw === 'object') return raw as Record<string, unknown>
  return null
}

/** 后端 gt/gte/lt/lte → 页面上因子名两侧的展示比较符 */
export function compareSymbolFromLeftOp(leftOp?: RangeBoundOp): string {
  return leftOp === 'gte' ? '<=' : '<'
}

export function compareSymbolFromRightOp(rightOp?: RangeBoundOp): string {
  return rightOp === 'lte' ? '<=' : '<'
}

export function leftOpFromCompareSymbol(sym?: string): 'gt' | 'gte' {
  return sym === '<=' ? 'gte' : 'gt'
}

export function rightOpFromCompareSymbol(sym?: string): 'lt' | 'lte' {
  return sym === '<=' ? 'lte' : 'lt'
}

function pickCompareField(obj: Record<string, unknown>, side: 'left' | 'right'): string | undefined {
  const keys =
    side === 'left'
      ? ['leftCompare', 'leftCompareChar', 'leftOpChar', 'leftSymbol', 'minCompare', 'gtCompare', 'gteCompare']
      : ['rightCompare', 'rightCompareChar', 'rightOpChar', 'rightSymbol', 'maxCompare', 'ltCompare', 'lteCompare']
  for (const key of keys) {
    const v = obj[key]
    if (v != null && String(v).trim() !== '') return String(v).trim()
  }
  return undefined
}

/** 解析后端下发的完整区间 descript，如 A股股东户数增长率:-54.37%<A股股东户数增长率<=0% */
export function parseRangeDisplayDescript(
  descript: string,
  factorName = '',
): ParsedRangeBounds | null {
  const text = String(descript || '').trim()
  if (!text) return null
  const m = text.match(/^(.+?)[：:]([-+]?[\d.]+%?)(<=|<)(.+?)(<=|<)([-+]?[\d.]+%?)$/)
  if (!m) return null
  const name = m[1].trim()
  if (factorName && name !== factorName.trim()) return null
  const leftCompare = m[3]
  const rightCompare = m[5]
  return {
    left: m[2],
    right: m[6],
    leftOp: leftOpFromCompareSymbol(leftCompare),
    rightOp: rightOpFromCompareSymbol(rightCompare),
    leftCompare,
    rightCompare,
  }
}

export function isBackendRangeDisplayDescript(descript: string, factorName = ''): boolean {
  const name = String(factorName || '').trim()
  const h = String(descript || '').trim()
  if (!h) return false
  if (name && h.startsWith(`${name}:`) && (h.includes('<=') || h.includes('<'))) return true
  if (!name && /[：:][-+]?[\d.]+%?(<=|<).+(<=|<)[-+]?[\d.]+%?$/.test(h)) return true
  return false
}

/** 从接口区间对象解析边界值与开闭区间运算符 */
export function extractRangeBounds(obj: Record<string, unknown>): ParsedRangeBounds | null {
  let left = ''
  let right = ''
  let leftOp: 'gt' | 'gte' = 'gte'
  let rightOp: 'lt' | 'lte' = 'lte'

  if (obj.gt != null) {
    left = String(obj.gt)
    leftOp = 'gt'
  } else if (obj.gte != null) {
    left = String(obj.gte)
    leftOp = 'gte'
  } else if (obj.min != null) {
    left = String(obj.min)
    leftOp = 'gte'
  }

  if (obj.lt != null) {
    right = String(obj.lt)
    rightOp = 'lt'
  } else if (obj.lte != null) {
    right = String(obj.lte)
    rightOp = 'lte'
  } else if (obj.max != null) {
    right = String(obj.max)
    rightOp = 'lte'
  }

  if (left === '' && right === '') return null

  const leftCompare = pickCompareField(obj, 'left') ?? compareSymbolFromLeftOp(leftOp)
  const rightCompare = pickCompareField(obj, 'right') ?? compareSymbolFromRightOp(rightOp)

  return { left, right, leftOp, rightOp, leftCompare, rightCompare }
}

export function parseWatchwordRangeBounds(apiItem: any, factorName = ''): ParsedRangeBounds | null {
  if (!apiItem || typeof apiItem !== 'object') return null

  const descript = apiItem.descript ?? apiItem.description
  if (typeof descript === 'string' && descript.trim()) {
    const fromDisplay = parseRangeDisplayDescript(descript, factorName)
    if (fromDisplay) return fromDisplay
  }

  const opts = apiItem.options
  if (Array.isArray(opts) && opts.length) {
    const obj = parseRangeOptionRaw(opts[0])
    if (obj) {
      const bounds = extractRangeBounds(obj)
      if (bounds) return bounds
    }
  }

  const top = extractRangeBounds(apiItem as Record<string, unknown>)
  if (top) return top

  if (typeof descript === 'string' && descript.trim()) {
    const m = descript.trim().match(/([\d.]+)\s*亿?\s*[~～\-—至]\s*([\d.]+)\s*亿?/)
    if (m) {
      return {
        left: m[1],
        right: m[2],
        leftOp: 'gte',
        rightOp: 'lte',
        leftCompare: compareSymbolFromLeftOp('gte'),
        rightCompare: compareSymbolFromRightOp('lte'),
      }
    }
  }
  return null
}

export function selectedRangeFromBounds(bounds: ParsedRangeBounds): SelectedRangeState {
  return {
    left: bounds.left,
    right: bounds.right,
    leftOp: bounds.leftOp,
    rightOp: bounds.rightOp,
    leftCompare: bounds.leftCompare ?? compareSymbolFromLeftOp(bounds.leftOp),
    rightCompare: bounds.rightCompare ?? compareSymbolFromRightOp(bounds.rightOp),
  }
}

function resolveRangeCompareSymbols(range: SelectedRangeState | null | undefined) {
  const leftSym =
    range?.leftCompare?.trim() ||
    compareSymbolFromLeftOp(range?.leftOp)
  const rightSym =
    range?.rightCompare?.trim() ||
    compareSymbolFromRightOp(range?.rightOp)
  return { leftSym, rightSym }
}

/** 提交回测/选股：按原运算符还原 options 项 */
export function buildRangeOptionFromSelectedRange(
  range: SelectedRangeState | null | undefined,
): Record<string, number> {
  const rangeObj: Record<string, number> = {}
  if (!range) return rangeObj
  const leftKey = range.leftOp === 'gt' ? 'gt' : 'gte'
  const rightKey = range.rightOp === 'lt' ? 'lt' : 'lte'
  if (range.left !== '' && range.left != null) rangeObj[leftKey] = Number(range.left)
  if (range.right !== '' && range.right != null) rangeObj[rightKey] = Number(range.right)
  return rangeObj
}

function formatRangeBoundValue(v: string, unit: string): string {
  const s = String(v)
  if (!unit) return s
  if (s.endsWith(unit)) return s
  return `${s}${unit}`
}

/** 区间因子展示：A股股东户数增长率:-54.37%<A股股东户数增长率<=0% */
export function formatFactorRangeDisplayLabel(
  name: string,
  range: SelectedRangeState | null | undefined,
  unit = '',
): string | null {
  const factorName = String(name || '').trim()
  if (!factorName || !range) return null
  const left = range.left !== '' && range.left != null ? String(range.left) : ''
  const right = range.right !== '' && range.right != null ? String(range.right) : ''
  if (!left && !right) return null
  const u = unit || ''
  const { leftSym, rightSym } = resolveRangeCompareSymbols(range)

  if (left && right) {
    return `${factorName}:${formatRangeBoundValue(left, u)}${leftSym}${factorName}${rightSym}${formatRangeBoundValue(right, u)}`
  }
  if (left) {
    return `${factorName}:${formatRangeBoundValue(left, u)}${leftSym}${factorName}`
  }
  if (right) {
    return `${factorName}:${factorName}${rightSym}${formatRangeBoundValue(right, u)}`
  }
  return null
}

/** 因子卡片副标题：去掉「因子名:」前缀，主标题仍显示因子名 */
export function formatFactorRangeSubLabel(
  name: string,
  range: SelectedRangeState | null | undefined,
  unit = '',
): string | null {
  const full = formatFactorRangeDisplayLabel(name, range, unit)
  const factorName = String(name || '').trim()
  if (!full || !factorName) return full
  const prefix = `${factorName}:`
  if (full.startsWith(prefix)) return full.slice(prefix.length)
  return full.startsWith(factorName) ? full.slice(factorName.length) : full
}

/** 区间因子提交 descript：仅条件表达式，不含「因子名:」前缀（因子名在 factorCode/接口其他字段体现） */
export function buildRangeDescriptFromSelectedRange(
  range: SelectedRangeState | null | undefined,
  opts?: { name?: string; unit?: string },
): string | null {
  const factorName = opts?.name ? String(opts.name).trim() : ''
  if (factorName) {
    return formatFactorRangeSubLabel(factorName, range, opts?.unit || '')
  }
  if (!range) return null
  const left = range.left !== '' && range.left != null ? String(range.left) : ''
  const right = range.right !== '' && range.right != null ? String(range.right) : ''
  if (!left && !right) return null
  if (left && right) return `${left} — ${right}`
  if (left) return `≥ ${left}`
  if (right) return `≤ ${right}`
  return null
}

export function mergeRangeOps(
  range: { left: string; right: string; leftOp?: RangeBoundOp; rightOp?: RangeBoundOp; leftCompare?: string; rightCompare?: string },
  fallback?: SelectedRangeState,
): SelectedRangeState {
  const leftOp = range.leftOp ?? fallback?.leftOp ?? 'gte'
  const rightOp = range.rightOp ?? fallback?.rightOp ?? 'lte'
  return {
    ...range,
    leftOp,
    rightOp,
    leftCompare:
      range.leftCompare ??
      fallback?.leftCompare ??
      compareSymbolFromLeftOp(leftOp),
    rightCompare:
      range.rightCompare ??
      fallback?.rightCompare ??
      compareSymbolFromRightOp(rightOp),
  }
}
